//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useCallback } from 'react';
import { useState, useEffect, useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
// import { Grid } from '@material-ui/core';
import { useParams, useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { logPageView } from '../system/firebase';
import { PATH_HOME } from '../system/constants';
import { LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import { loadProfileByUserId, loadProfileByCustomName } from '../common/services/dashboardServices';
import { PAGE_TITLE_TALENT_PROFILE } from '../common/constants';
import { getFullNameByUser } from '../common/textHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import TopNav from '../topNav/TopNav';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
import AfitdEditDialog from '../common/elemental/AfitdEditDialog';
import JobSeekerProfile from '../empTalentCenter/subComponents/JobSeekerProfile';
import TalentPageUnavailable from './subComponents/TalentPageUnavailable';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    pageContainer: {
        backgroundColor: '#FAFAFC',
        overflow: 'hidden',
        height: '100%',
        minHeight: '100%',
        flexWrap: 'unset',
    },
    pageHeader: { maxHeight: '81px' },
    pageContent: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingLeft: '64px',
        paddingRight: '64px',
    },
    jobCenterLeft: { height: '100%', paddingTop: '25px' },
    jobCenterRight: {
        height: '100%',
        overflowY: 'hidden',
        flexWrap: 'unset',
        paddingTop: '35px',
        minHeight: '600px',
    },
    optionMenuContainer: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        marginTop: '-45px',
    },
    badge: {
        marginLeft: '20px',
        marginRight: '10px',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        // talentUserId,
        // talentUsername,
        // employer,
        // setEmployer,
        // handleChangeMenu,
        // selectedMenu,
        talentProfileDialogOpen,
        // setTalentProfileDialogOpen,
        handleCloseTalentPageDialog,
        profileData,
    } = params;
    const { jobSeekerApplySavedDt, isSaved, isHideSavedToMyJobs } = props;

    const jobSeekerName = getFullNameByUser(profileData);
    const pageTitle = `${PAGE_TITLE_TALENT_PROFILE} | ${jobSeekerName}`;

    const defns = {
        pageHead: { title: pageTitle },
        pageContainer: { container: true, direction: 'column' },
        pageHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        pageContent: { item: true, container: true },
        contentContainer: { item: true, container: true, direction: 'column' },
        brandPageDialog: {
            fullScreen: true,
            open: talentProfileDialogOpen,
            // isDirty,
            onClose: handleCloseTalentPageDialog,
            hideActions: true,
        },
        talentPageNotAvailable: {
            profileData,
        },
        jobSeekerProfile: {
            jobSeeker: { data: profileData },
            isInDialog: false,
            jobSeekerApplySavedDt,
            isSaved,
            isHideSavedToMyJobs,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JobSeekerProfileLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const history = useHistory();

    //---------------------------------------------------------------------
    // Params
    //---------------------------------------------------------------------
    const { talentUserId, talentUsername } = useParams();

    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    const context = useContext(LayoutContext);
    // const { setSnackbarMessage, setSnackbarErrorMessage } = context;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { isShowJobs } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [profileData, setProfileData] = useState(null);
    // const [employer, setEmployer] = useState(null);
    // const [selectedMenu, setSelectedMenu] = useState('info');
    const [talentProfileDialogOpen, setTalentProfileDialogOpen] = useState(false);
    const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isPublic = _get(profileData, 'isPublic'); // do not set default value as we want the undefined state in order to show blank
    const jobSeekerName = getFullNameByUser(profileData);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        jobSeekerName && logPageView(PAGE_TITLE_TALENT_PROFILE, { name: jobSeekerName });
    }, [jobSeekerName]);

    // get the Talent profile by id
    useEffect(() => {
        if (talentUserId) loadProfileByUserId(talentUserId, setProfileData);
    }, [talentUserId, profileData]);

    // when data are ready, open dialog
    useEffect(() => {
        if (profileData) setTalentProfileDialogOpen(true);
    }, [profileData]);

    // viewing Talent Page with custom name, fetch profile data first
    useEffect(() => {
        if (talentUsername) {
            // a work-around to delay the firebase call so the app can call firebase.initializeApp() first.
            setTimeout(() => {
                loadProfileByCustomName({
                    userName: talentUsername,
                    setData: setProfileData,
                });
            }, 200);
        }
    }, [talentUsername]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    // const handleChangeMenu = item => {
    //     setSelectedMenu(item);
    // };

    const handleCloseTalentPageDialog = useCallback(() => {
        history.push(`${PATH_HOME}`);
        setTalentProfileDialogOpen(false);
        setProfileData(null);
    }, [history]);

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        talentUserId,
        talentUsername,
        // employer,
        // setEmployer,
        // handleChangeMenu,
        // selectedMenu,
        talentProfileDialogOpen,
        setTalentProfileDialogOpen,
        handleCloseTalentPageDialog,
        interviewDialogOpen,
        setInterviewDialogOpen,
        profileData,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context, profileData }}>
            <AfitdPageHead {...defns.pageHead} />
            {talentProfileDialogOpen && (
                <AfitdEditDialog {...defns.brandPageDialog}>
                    {isPublic === true && <JobSeekerProfile {...defns.jobSeekerProfile} />}
                    {isPublic === false && <TalentPageUnavailable {...defns.talentPageNotAvailable} />}
                </AfitdEditDialog>
            )}
        </LayoutContext.Provider>
    );
};

export default JobSeekerProfileLayout;
